import { ContentType } from '../../enums/ContentType';
import { Language } from '../../enums/Language';
import { SponsorModel, PartnerModel } from '../../models';
import { MultiLanguageTextModel } from '../../models/MultiLanguageTextModel';
import { BoundsShape } from '../../models/QueryModel';
import SponsorRepository from '../SponsorRepository';
import { apiFetch } from '../../utils/apiFetch';

export default class BeatAPISponsorRepository extends SponsorRepository {
  private _url: string;
  private _apiKey: string;
  private _partnersBaseUrl: string | null;

  constructor(url: string, apiKey: string, partnersBaseUrl: string | null = null) {
    super();
    this._url = url;
    this._apiKey = apiKey;
    this._partnersBaseUrl = partnersBaseUrl;
  }

  set url(url: string) {
    this._url = url;
  }

  set apiKey(apiKey: string) {
    this._apiKey = apiKey;
  }

  set partnersBaseUrl(value: string | null) {
    this._partnersBaseUrl = value;
  }

  /**
   * Fetch a single partner by id, independent of the current map location.
   * Used when a banner / zeppelin click references a partner that may not be
   * in the location-derived set returned by `get()`.
   */
  async getById(id: string): Promise<PartnerModel | null> {
    if (!this._partnersBaseUrl) {
      return null;
    }
    const response = await apiFetch(`${this._partnersBaseUrl}/${id}`, {
      method: 'GET',
      apiKey: this._apiKey,
    });
    if (!response.ok) {
      return null;
    }
    // showOne returns a single PartnerResource wrapped in Laravel's `data`
    // envelope: { data: { id, name, ... } }. Unwrap it (mirrors get(), which
    // reads `.data`); reading the top-level `id` would always be undefined.
    const json = await response.json();
    const s = json?.data ?? json;
    if (!s || !s.id) {
      return null;
    }
    const desc = (s.descriptions ?? []).find(() => true);
    const partner = new PartnerModel(
      s.id,
      s.name,
      s.websiteUrl,
      new MultiLanguageTextModel(
        desc
          ? [
              {
                language: Language.de,
                text: desc.de,
                contentType: ContentType.TEXT,
              },
              {
                language: Language.en,
                text: desc.en,
                contentType: ContentType.TEXT,
              },
            ]
          : []
      ),
      s.brandLogo,
      s.email,
      s.phone,
      s.address,
      s.htmlDescription || null
    );
    partner.partnerCode = s.partnerCode ?? null;
    return partner;
  }

  async get(bounds: BoundsShape): Promise<{
    sponsors: SponsorModel[];
    regionalPartners: PartnerModel[];
  }> {
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const response = await apiFetch(this._url, {
      method: 'POST',
      // Send the viewport bounding box (matches /api/v2/banners). The backend
      // returns partners whose region CENTRE falls inside it, so a large region
      // only surfaces its partner when its centre is on screen.
      body: JSON.stringify({
        top_left: [ne.lat, sw.lng],
        bottom_right: [sw.lat, ne.lng],
      }),
      apiKey: this._apiKey,
    });
    const partners = (await response.json()).data || [];
    const sponsors = partners
      .filter((s: any) =>
        s.type.find((t: any) => t.slug === 'supporting-partner')
      )
      .map((s: any) => {
        const desc = s.descriptions.find(() => true);
        const sponsor = new SponsorModel(
          s.id,
          s.name,
          s.websiteUrl,
          new MultiLanguageTextModel(
            desc
              ? [
                  {
                    language: Language.de,
                    text: desc.de,
                    contentType: ContentType.TEXT,
                  },
                  {
                    language: Language.en,
                    text: desc.en,
                    contentType: ContentType.TEXT,
                  },
                ]
              : []
          ),
          s.brandLogo,
          s.email,
          s.phone,
          s.address,
          s.htmlDescription || null,
          s.zeppelinImage
        );
        sponsor.partnerCode = s.partnerCode ?? null;
        return sponsor;
      });
    const regionalPartners = partners
      .filter((s: any) =>
        s.type.find((t: any) => t.slug === 'regional-partner')
      )
      .map((s: any) => {
        const desc = s.descriptions.find(() => true);
        const partner = new PartnerModel(
          s.id,
          s.name,
          s.websiteUrl,
          new MultiLanguageTextModel(
            desc
              ? [
                  {
                    language: Language.de,
                    text: desc.de,
                    contentType: ContentType.TEXT,
                  },
                  {
                    language: Language.en,
                    text: desc.en,
                    contentType: ContentType.TEXT,
                  },
                ]
              : []
          ),
          s.brandLogo,
          s.email,
          s.phone,
          s.address,
          s.htmlDescription || null
        );
        partner.partnerCode = s.partnerCode ?? null;
        return partner;
      });
    return {
      regionalPartners,
      sponsors,
    };
  }
}
